import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";
import SEO from "../components/SEO";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "mainEntity": {
    "@type": "LocalBusiness",
    "@id": "https://www.barspotltd.co.uk/#business",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+447554856712",
      "contactType": "customer service",
      "email": "hello@barspotltd.co.uk",
      "availableLanguage": "English",
      "areaServed": "GB"
    }
  }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-20">
      <SEO
        title="Contact Bar Spot | Get a Free Mobile Bar Hire Quote"
        description="Get in touch for a free mobile bar hire quote. Call 07554 856712 or email hello@barspotltd.co.uk. Based in Birmingham & Cambridge. Response within 24 hours."
        canonical="/contact"
        schema={contactSchema}
      />
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="text-[#a5dae0] font-script text-2xl mb-4">Get In Touch</p>
              <h1 className="text-5xl mb-6">Contact Us</h1>
              <p className="text-[#666666] leading-relaxed mb-8">
                Have a question or ready to book your mobile bar? Get in touch and we'll respond within 24 hours.
                We serve Birmingham, Cambridge, the Midlands and across the UK.
              </p>

              <div className="space-y-6">
                <a href="tel:07554856712" className="flex items-center gap-4 text-[#444444] hover:text-[#a5dae0] transition-colors">
                  <div className="w-12 h-12 bg-[#a5dae0]/10 rounded-full flex items-center justify-center">
                    <Phone size={20} className="text-[#a5dae0]" />
                  </div>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p>07554 856712</p>
                  </div>
                </a>
                <a href="mailto:hello@barspotltd.co.uk" className="flex items-center gap-4 text-[#444444] hover:text-[#a5dae0] transition-colors">
                  <div className="w-12 h-12 bg-[#a5dae0]/10 rounded-full flex items-center justify-center">
                    <Mail size={20} className="text-[#a5dae0]" />
                  </div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <p>hello@barspotltd.co.uk</p>
                  </div>
                </a>
                <div className="flex items-start gap-4 text-[#444444]">
                  <div className="w-12 h-12 bg-[#a5dae0]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#a5dae0]" />
                  </div>
                  <div>
                    <p className="font-semibold">Address</p>
                    <p>Unit A, 82 James Carter Road,<br />Mildenhall, Suffolk, IP28 7DE</p>
                  </div>
                </div>
              </div>

              {/* Quick links */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-[#555555] mb-3">Or explore our services:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/weddings" className="text-sm text-[#a5dae0] hover:underline">Wedding Bar Hire</Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/private-hire" className="text-sm text-[#a5dae0] hover:underline">Corporate Events</Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/festivals" className="text-sm text-[#a5dae0] hover:underline">Festivals</Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/menu" className="text-sm text-[#a5dae0] hover:underline">Drinks Menu</Link>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-[#444444] mb-2">Name *</label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="contact-name"
                      autoComplete="name"
                      className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-0 outline-none transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-[#444444] mb-2">Email *</label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="contact-email"
                        autoComplete="email"
                        className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-0 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-[#444444] mb-2">Phone</label>
                      <input
                        id="contact-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        data-testid="contact-phone"
                        autoComplete="tel"
                        className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-0 outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-[#444444] mb-2">Subject *</label>
                    <input
                      id="contact-subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      data-testid="contact-subject"
                      className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-0 outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-[#444444] mb-2">Message *</label>
                    <textarea
                      id="contact-message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      data-testid="contact-message"
                      className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-0 outline-none transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                    className="w-full bg-[#a5dae0] text-[#1a1a1a] py-4 rounded-full font-semibold hover:bg-[#8bc9d0] transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
